import React, { useState, useMemo, useEffect } from "react";
import { Typography, Tabs, Spin, Card } from "antd";
import { ThunderboltOutlined } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { generateLiveOptionsData } from "./utils/liveDataGenerator";

// Hooks
import { useOptionCalculations } from "./hooks/useOptionCalculations";
import { useScenarioData } from "./hooks/useScenarioData";
import { useRiskAnalysis } from "./hooks/useRiskAnalysis";
import { useMatrixData } from "./hooks/useMatrixData";

// Components
import OptionsInputForm from "./components/OptionsInputForm";
import EmptyState from "./components/EmptyState";
import ControlsCard from "./components/ControlsCard";
import BestOptionAlert from "./components/BestOptionAlert";
import CompleteOverviewTab from "./components/CompleteOverviewTab";
import RobustRankingsTab from "./components/RobustRankingsTab";
import RiskAnalysisTab from "./components/RiskAnalysisTab";

const { Title } = Typography;

const OptionSimulator = () => {
  const { symbol: urlSymbol } = useParams();
  const navigate = useNavigate();

  const [selectedSymbol, setSelectedSymbol] = useState(urlSymbol || null);
  const [optionsData, setOptionsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedExpiration, setSelectedExpiration] = useState(null);
  const [selectedStrike, setSelectedStrike] = useState(null);
  const [expectedMove, setExpectedMove] = useState(5);
  const [expectedDays, setExpectedDays] = useState(3);
  const [calculationParams, setCalculationParams] = useState(null);

  const currentExpirationData = useMemo(
    () =>
      optionsData && selectedExpiration
        ? optionsData[selectedExpiration]
        : null,
    [optionsData, selectedExpiration],
  );

  const strikes = useMemo(() => {
    const allStrikes = currentExpirationData?.strikes || [];
    // Filter out strikes with entry price less than $0.30
    return allStrikes.filter((strike) => {
      const firstTradingDay = strike.trading_days[0];
      return firstTradingDay?.theoretical_price_now >= 0.3;
    });
  }, [currentExpirationData]);

  // Custom hooks for data calculations
  const { calculateBestOptions } = useOptionCalculations({
    strikes,
    expectedMove,
    expectedDays,
  });

  const currentStrikeData = useMemo(() => {
    if (!selectedStrike) return null;
    return strikes.find((s) => s.strike === selectedStrike);
  }, [strikes, selectedStrike]);

  const { strikeComparisonData } = useScenarioData({
    currentStrikeData,
    expectedDays,
    expectedMove,
    strikes,
  });

  const { riskAnalysisData, profitLossChartData } = useRiskAnalysis({
    currentStrikeData,
    expectedDays,
    expectedMove,
  });

  const {
    comprehensiveMatrixData,
    heatmapScale,
    allTradingDays,
    multiStrikeTimeDecayData,
  } = useMatrixData({ strikes, expectedMove });

  const bestOption = calculateBestOptions?.[0];

  // Handle symbol change — clear all prior results; fetching/calculation is
  // handled by OptionsInputForm.
  const handleSymbolChange = (symbol) => {
    setSelectedSymbol(symbol);
    setOptionsData(null);
    setCalculationParams(null);
    setSelectedExpiration(null);
    setSelectedStrike(null);

    if (symbol) {
      navigate(`/option-simulator/${symbol}`);
    } else {
      navigate("/option-simulator");
    }
  };

  // Clear the currently displayed results as soon as a new expiration is
  // picked, so stale data from the previous expiration isn't shown while
  // its chain is being fetched.
  const handleExpirationSelect = () => {
    setOptionsData(null);
    setCalculationParams(null);
    setSelectedExpiration(null);
    setSelectedStrike(null);
  };

  // Handle calculation — always generated client-side from a real fetched
  // live option chain (each strike priced with its own live IV).
  const handleCalculate = (params) => {
    setLoading(true);
    try {
      const data = generateLiveOptionsData(params);
      setOptionsData(data);
      setSelectedStrike(null);
      setSelectedExpiration(Object.keys(data)[0]);
      setCalculationParams(params);
    } catch (error) {
      console.error("Error generating options data:", error);
      setOptionsData(null);
    } finally {
      setLoading(false);
    }
  };

  // Recompute client-side (no re-fetch) when the user edits Underlying
  // Price, Reference Date, or Risk-Free Rate for the currently loaded chain.
  const handleLiveUnderlyingPriceChange = (value) => {
    if (!calculationParams || value === null) return;
    const updated = { ...calculationParams, underlyingPrice: value };
    setOptionsData(generateLiveOptionsData(updated));
    setCalculationParams(updated);
  };

  const handleLiveReferenceDateChange = (date) => {
    if (!calculationParams || !date) return;
    const updated = { ...calculationParams, time: date };
    setOptionsData(generateLiveOptionsData(updated));
    setCalculationParams(updated);
  };

  const handleRiskFreeRateChange = (value) => {
    if (!calculationParams || value === null) return;
    const updated = { ...calculationParams, riskFreeRate: value };
    setOptionsData(generateLiveOptionsData(updated));
    setCalculationParams(updated);
  };

  // Set default strike when strikes are available
  useEffect(() => {
    if (strikes.length > 0 && !selectedStrike) {
      setSelectedStrike(strikes[0].strike);
    }
  }, [strikes, selectedStrike]);

  // Set page title
  useEffect(() => {
    const symbolPart = selectedSymbol ? ` - ${selectedSymbol}` : "";
    document.title = `Options Simulator${symbolPart} | Dindicator Dashboard`;
  }, [selectedSymbol]);

  return (
    <div style={{ padding: "24px", minHeight: "100vh" }}>
      <Title level={2}>
        <ThunderboltOutlined /> Options Simulator - Risk/Reward Analyzer
      </Title>

      <OptionsInputForm
        selectedSymbol={selectedSymbol}
        onSymbolChange={handleSymbolChange}
        onCalculate={handleCalculate}
        onExpirationSelect={handleExpirationSelect}
      />

      {loading && (
        <Card style={{ textAlign: "center", padding: "48px", marginTop: 24 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Calculating options...</div>
        </Card>
      )}

      {!loading && !optionsData && <EmptyState />}

      {!loading && optionsData && (
        <>
          <ControlsCard
            selectedExpiration={selectedExpiration}
            expectedMove={expectedMove}
            setExpectedMove={setExpectedMove}
            expectedDays={expectedDays}
            setExpectedDays={setExpectedDays}
            currentExpirationData={currentExpirationData}
            riskFreeRate={calculationParams?.riskFreeRate}
            onRiskFreeRateChange={handleRiskFreeRateChange}
            liveUnderlyingPrice={calculationParams?.underlyingPrice}
            onLiveUnderlyingPriceChange={handleLiveUnderlyingPriceChange}
            liveReferenceDate={
              calculationParams?.time ? dayjs(calculationParams.time) : null
            }
            onLiveReferenceDateChange={handleLiveReferenceDateChange}
          />

          <BestOptionAlert
            bestOption={bestOption}
            expectedDays={expectedDays}
            expectedMove={expectedMove}
          />

          <Tabs
            defaultActiveKey="1"
            items={[
              {
                key: "1",
                label: "📊 Complete Overview",
                children: (
                  <CompleteOverviewTab
                    comprehensiveMatrixData={comprehensiveMatrixData}
                    heatmapScale={heatmapScale}
                    allTradingDays={allTradingDays}
                    expectedMove={expectedMove}
                    calculateBestOptions={calculateBestOptions}
                    selectedStrike={selectedStrike}
                    setSelectedStrike={setSelectedStrike}
                    multiStrikeTimeDecayData={multiStrikeTimeDecayData}
                    strikes={strikes}
                    strikeComparisonData={strikeComparisonData}
                    bestOption={bestOption}
                    underlyingPrice={calculationParams?.underlyingPrice}
                  />
                ),
              },
              {
                key: "2",
                label: "🏆 Robust Rankings",
                children: (
                  <RobustRankingsTab
                    calculateBestOptions={calculateBestOptions}
                    expectedDays={expectedDays}
                    expectedMove={expectedMove}
                    setSelectedStrike={setSelectedStrike}
                    selectedStrike={selectedStrike}
                  />
                ),
              },
              {
                key: "3",
                label: "⚠️ Risk Analysis",
                children: (
                  <RiskAnalysisTab
                    riskAnalysisData={riskAnalysisData}
                    profitLossChartData={profitLossChartData}
                    selectedStrike={selectedStrike}
                    setSelectedStrike={setSelectedStrike}
                    strikes={strikes}
                    expectedMove={expectedMove}
                    currentStrikeData={currentStrikeData}
                  />
                ),
              },
            ]}
          />
        </>
      )}
    </div>
  );
};

export default OptionSimulator;
